/**
 * Every deployment environment the app can be built for.
 *
 * One list feeds the zod enums in `lib/env.ts` and the `AppEnv` type used by
 * screen gating, sign-in validation and the audit-log viewer, so adding an
 * environment is a one-line change that cannot drift between them.
 *
 * `demo` is the Predictive-K sales demo: a separate deployment that runs this
 * code on anonymized data. It gets none of the dev-only allowances (auth
 * fallbacks and the like). Its one difference from prod on screens is the
 * analysis bands below, which it shows; every other screen tagged
 * `envs: ["dev"]` stays hidden exactly as it is in prod.
 */
export const APP_ENVS = ["dev", "qa", "prod", "demo"] as const;

export type AppEnv = (typeof APP_ENVS)[number];

/**
 * Where the analysis screens show: the sidebar's Diagnostic, Predictive,
 * Impact, Prescriptive and Scenarios bands, and the pages among them that
 * gate themselves (E-10). Dev, where they are built, and the sales demo,
 * which sells them (DEC-10). QA and prod keep publishing only the overview
 * flow and the screens it links out to.
 *
 * One list, so the sidebar entry and a page's own guard cannot disagree: one
 * without the other is a nav entry that 404s, or a page nobody can find.
 */
export const ANALYSIS_ENVS: readonly AppEnv[] = ["dev", "demo"];

/**
 * Where the admin wiki ships (E-04e): every environment of FE's own product.
 * The wiki is FE's documentation of its models, written with FE's figures
 * and places, so another deployment of this code — the sales demo — gets
 * neither the nav entry nor the page. The two wiki pages drop their import
 * in a `demo` build (see `app/(app)/admin/wiki/*`), so the content is not in
 * that bundle at all, not merely hidden.
 */
export const WIKI_ENVS: AppEnv[] = APP_ENVS.filter((env) => env !== "demo");
